import clsx from 'clsx';
import React, {
  useCallback,
  useState
} from 'react';
import { FiX } from 'react-icons/fi';
import './configPane.scss';

import { useAgentEdit } from '@/app/agents/edit/state/agentEditContext';
import { AgentEditActionType } from '@/app/agents/edit/state/agentEditReducer';
import { getNodeTypeIcon, getNodeTypeLabel } from '@/app/agents/edit/util/nodeTypeUtils';
import { DeleteModal } from '@/components/deleteModal/deleteModal';
import { Heading } from '@/components/typography/heading';
import {
  AgentConfigGlobalContexts,
  AgentConfigStep,
  AgentConfigStepAction,
  AgentConfigStepInputs,
  AgentConfigStepRoutes,
  AgentConfigStepTypeEnum
} from '@/types/agent';

import styles from './configEditPanel.module.scss';
import PanelAction from './panelAction';
import PanelEnd from './panelEnd';
import PanelStart from './panelStart';


export type Props = {
  context?: AgentConfigGlobalContexts;
  editable: boolean;
  isFullScreen: boolean;
  modelNames: string[];
  onClose: () => void;
  onDelete: () => void;
  step: AgentConfigStep;
  stepNames: string[];
};

export function ConfigEditPanel( props: Props ) {
  const {
    context = {},
    editable,
    modelNames,
    onClose,
    onDelete,
    step,
    stepNames,
    isFullScreen
  } = props;

  const { dispatch } = useAgentEdit();
  const [ showModalDeleteConfirmation, setShowModalDeleteConfirmation ] = useState<boolean>( false );

  // All handlers are stable — dispatch and step.id never change for this
  // component's lifetime (key={activeStepIndex} remounts it when switching steps).
  const handleDeleteClick = useCallback( () => {
    setShowModalDeleteConfirmation( true );
  }, [] );

  const onChangeContext = useCallback( ( context: AgentConfigGlobalContexts ) => {
    dispatch( { type: AgentEditActionType.CONFIG_UPDATE_CONTEXT, context } );
  }, [ dispatch ] );

  const onChangeStepAction = useCallback( <T extends AgentConfigStepAction>( action: T ) => {
    dispatch( { type: AgentEditActionType.STEP_UPDATE_ACTION, stepId: step.id, action } );
  }, [ dispatch, step.id ] );

  const onChangeStepDescription = useCallback( ( event: React.ChangeEvent<HTMLTextAreaElement> ) => {
    dispatch( { type: AgentEditActionType.STEP_UPDATE_DESCRIPTION, stepId: step.id, value: event.currentTarget.value } );
  }, [ dispatch, step.id ] );

  const onChangeStepFirstRoute = useCallback( ( e: React.ChangeEvent<HTMLSelectElement> ) => {
    dispatch( { type: AgentEditActionType.STEP_UPDATE_FIRST_ROUTE, stepId: step.id, value: e.currentTarget.value } );
  }, [ dispatch, step.id ] );

  const onChangeStepInputs = useCallback( ( inputs: AgentConfigStepInputs ) => {
    dispatch( { type: AgentEditActionType.STEP_UPDATE_INPUTS, stepId: step.id, inputs } );
  }, [ dispatch, step.id ] );

  const onChangeStepName = useCallback( ( event: React.FormEvent<HTMLInputElement> ) => {
    dispatch( { type: AgentEditActionType.STEP_UPDATE_NAME, stepId: step.id, value: event.currentTarget.value } );
  }, [ dispatch, step.id ] );

  const onChangeStepRoutes = useCallback( ( routes: AgentConfigStepRoutes ) => {
    dispatch( { type: AgentEditActionType.STEP_UPDATE_ROUTES, stepId: step.id, routes } );
  }, [ dispatch, step.id ] );

  const renderPanes = () => {
    switch ( step.type ) {
      case AgentConfigStepTypeEnum.START:
        return (
          <PanelStart
            globalContext={ context }
            editable={ editable }
            onChangeContext={ onChangeContext }
            onChangeFirstRoute={ onChangeStepFirstRoute }
            onChangeInputs={ onChangeStepInputs }
            step={ step }
            stepNames={ stepNames }
          />
        );
      case AgentConfigStepTypeEnum.END:
        return <PanelEnd />;
      default:
        return (
          <PanelAction
            globalContext={ context }
            editable={ editable }
            modelNames={ modelNames }
            onChangeAction={ onChangeStepAction }
            onChangeDescription={ onChangeStepDescription }
            onChangeInputs={ onChangeStepInputs }
            onChangeName={ onChangeStepName }
            onChangeRoutes={ onChangeStepRoutes }
            onDeleteClick={ handleDeleteClick }
            step={ step }
            stepNames={ stepNames }
          />
        );
    }
  };

  return (
    <>
      <DeleteModal
        isOpen={ showModalDeleteConfirmation }
        onClose={ () => setShowModalDeleteConfirmation( false ) }
        onDelete={ onDelete }
        entityName={ step.id }
        entityType='Node'
        overrideWarning={ (
          <p>This action can't be undone.</p>
        ) }
        body={ (
          <>
            <p>Deleting <strong>{ step.id }</strong>:</p>
            <ul>
              <li>will remove the node from this agent</li>
              <li>may break step connections within this agent's flow</li>
              <li>may disrupt the workings of this agent</li>
            </ul>
          </>
        ) }
      />

      <div data-testid="config-edit-panel" className={ clsx( styles.configBox, isFullScreen && styles.fullscreen ) }>
        <div className={ styles.header }>
          <Heading as="h3" size="sm">
            Edit { getNodeTypeLabel( step.type ) } Node
            <span className={ styles.header_icon }>{ getNodeTypeIcon( step.type ) }</span>
          </Heading>
          <FiX className={ styles.back_button } data-testid="config-edit-panel-close-button" onClick={ onClose } />
        </div>
        { renderPanes() }
      </div>
    </>
  );
}
